import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router'
import { Search, SlidersHorizontal, Headset, ArrowRightLeft, Users, Bot } from 'lucide-react'
import { FacebookIcon, WhatsAppIcon, InstagramIcon, TelegramIcon } from '../../../modules/conversations/BrandIcons.js'
import { useAuthStore } from '../../../store/authStore.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useLiveChatUnreadStore } from '../../../store/liveChatUnreadStore.js'
import { useUnifiedChats, type ChatChannel, type ChatStatus } from '../../../hooks/useUnifiedChats.js'
import { useChatTransfers } from '../../../hooks/useChatTransfers.js'
import { useAssignableAgents } from '../../../hooks/useAssignableAgents.js'
import ActionDrawer from '../../../components/conversations/detail/ActionDrawer.js'
import { Field, GhostButton, PrimaryButton, SelectField, htmlToText } from '../../../components/conversations/detail/primitives.js'
import { api } from '../../../api/client.js'
import { getSocket } from '../../../socket/client.js'
import RichTextEditor, { isEmptyHtml } from '../../../components/common/RichTextEditor.js'
import ChatConversationList from './ChatConversationList.js'
import ChatDetailsPanel from './ChatDetailsPanel.js'
import LiveChatThread from './LiveChatThread.js'
import { WhatsAppChatThread } from '../../../modules/whatsapp-chat/WhatsAppChatThread.js'
import { FacebookChatThread } from '../../../modules/facebook-chat/FacebookChatThread.js'
import { InstagramChatThread } from '../../../modules/instagram-chat/InstagramChatThread.js'
import { TelegramChatThread } from '../../../modules/telegram-chat/TelegramChatThread.js'
import { WhatsAppCallOverlay } from '../../../modules/whatsapp-chat/WhatsAppCallOverlay.js'
import { useWhatsAppCall, useIncomingWhatsAppCalls } from '../../../hooks/useWhatsAppCall.js'
import { useWhatsAppChats } from '../../../hooks/useWhatsAppChats.js'

const FILTERS: { key: ChatStatus; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'mine', label: 'Mine' },
  { key: 'unread', label: 'Unread' },
]

const CHANNELS: {
  key: ChatChannel
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  activeBg: string
}[] = [
  {
    key: 'chat',
    label: 'Web',
    icon: Headset,
    color: 'text-indigo-600',
    activeBg: 'bg-indigo-600',
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: WhatsAppIcon,
    color: 'text-green-600',
    activeBg: 'bg-green-600',
  },
  {
    key: 'telegram',
    label: 'Telegram',
    icon: TelegramIcon,
    color: 'text-blue-500',
    activeBg: 'bg-blue-500',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: FacebookIcon,
    color: 'text-blue-600',
    activeBg: 'bg-blue-600',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    icon: InstagramIcon,
    color: 'text-pink-600',
    activeBg: 'bg-pink-600',
  },
  
]

const VALID_CHANNELS = new Set<ChatChannel>(['chat', 'facebook', 'instagram', 'whatsapp', 'telegram'])

export default function ChatPage(): JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const params = useParams<{ chatId?: string }>()

  const channel = useMemo<ChatChannel>(() => {
    const path = pathname.toLowerCase()
    if (path === '/agent/live-chat' || path.startsWith('/agent/live-chat/')) return 'chat'
    const match = /^\/agent\/chats\/(whatsapp|facebook|instagram|telegram)(?:\/|$)/.exec(path)
    if (match) return match[1] as ChatChannel
    const q = searchParams.get('channel') as ChatChannel
    if (q && VALID_CHANNELS.has(q)) return q
    return 'chat'
  }, [pathname, searchParams])

  const initialConversationId = params.chatId ?? searchParams.get('conversationId')
  const [status, setStatus] = useState<ChatStatus>('all')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(initialConversationId)
  const [showDetails, setShowDetails] = useState(true)
  const [listWidth, setListWidth] = useState(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('kc3-chat-list-width') : null
    return saved ? Number(saved) : 520
  })
  const [detailsWidth, setDetailsWidth] = useState(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('kc3-chat-details-width') : null
    return saved ? Number(saved) : 640
  })
  const [resizing, setResizing] = useState<{ side: 'list' | 'details'; startX: number; startWidth: number } | null>(null)
  const [transferDrawerOpen, setTransferDrawerOpen] = useState(false)
  const [selectedTransferTeam, setSelectedTransferTeam] = useState('')
  const [selectedTransferAgent, setSelectedTransferAgent] = useState('')
  const [transferReason, setTransferReason] = useState('')
  const [transferringToAi, setTransferringToAi] = useState(false)

  const user = useAuthStore((s) => s.user)
  const toast = useToast()
  const { entries, counts, loading, markRead, refetch } = useUnifiedChats(channel, status, search)
  const setStoreSelectedId = useLiveChatUnreadStore((s) => s.setSelectedId)
  const lastSelectedEntryRef = useRef<import('./ChatConversationList.js').ChatListEntry | null>(null)
  const selectedEntry = useMemo(() => {
    const found = entries.find((e) => e.id === selectedId)
    if (found) {
      lastSelectedEntryRef.current = found
      return found
    }
    if (lastSelectedEntryRef.current && lastSelectedEntryRef.current.id === selectedId) {
      return lastSelectedEntryRef.current
    }
    return null
  }, [entries, selectedId])

  useEffect(() => {
    const onEnded = (e: Event): void => {
      const detail = (e as CustomEvent<{ chatId?: string }>).detail
      if (detail?.chatId && lastSelectedEntryRef.current?.id === detail.chatId) {
        lastSelectedEntryRef.current = {
          ...lastSelectedEntryRef.current,
          status: 'abandoned',
        }
        refetch()
      }
    }
    const onResumed = (e: Event): void => {
      const detail = (e as CustomEvent<{ chatId?: string | undefined; assignedAgentId?: string | undefined }>).detail
      if (detail?.chatId && lastSelectedEntryRef.current?.id === detail.chatId) {
        lastSelectedEntryRef.current = {
          ...lastSelectedEntryRef.current,
          status: 'active',
          ...(detail.assignedAgentId ? { assignedAgentId: detail.assignedAgentId } : {}),
        }
        refetch()
      }
    }
    window.addEventListener('kc3:chat:ended', onEnded)
    window.addEventListener('kc3:chat:resumed', onResumed)

    const socket = getSocket()
    const onSocketEnded = (data: { chatId?: string | undefined }): void => {
      if (data?.chatId && lastSelectedEntryRef.current?.id === data.chatId) {
        lastSelectedEntryRef.current = {
          ...lastSelectedEntryRef.current,
          status: 'abandoned',
        }
        refetch()
      }
    }
    const onSocketResumed = (data: { chatId?: string | undefined; assignedAgentId?: string | undefined }): void => {
      if (data?.chatId && lastSelectedEntryRef.current?.id === data.chatId) {
        lastSelectedEntryRef.current = {
          ...lastSelectedEntryRef.current,
          status: 'active',
          ...(data.assignedAgentId ? { assignedAgentId: data.assignedAgentId } : {}),
        }
        refetch()
      }
    }
    socket.on('live:chat:ended', onSocketEnded)
    socket.on('live:chat:resumed', onSocketResumed)

    return () => {
      window.removeEventListener('kc3:chat:ended', onEnded)
      window.removeEventListener('kc3:chat:resumed', onResumed)
      socket.off('live:chat:ended', onSocketEnded)
      socket.off('live:chat:resumed', onSocketResumed)
    }
  }, [refetch])

  const { transfers, transferring, transfer, refetch: refetchTransfers } = useChatTransfers(selectedEntry?.id ?? null, selectedEntry?.channel ?? null)
  const { agents: assignableAgents, teams: assignableTeams } = useAssignableAgents(true)

  // WhatsApp calling
  const { account: waAccount } = useWhatsAppChats()
  const waCall = useWhatsAppCall()
  useIncomingWhatsAppCalls(!waCall.isBusy, waCall.setIncoming)

  const handleSelect = useCallback(
    (id: string): void => {
      setSelectedId(id)
      markRead(id, channel)
    },
    [markRead, channel],
  )

  useEffect(() => {
    if (
      initialConversationId &&
      selectedId !== initialConversationId &&
      entries.some((e) => e.id === initialConversationId)
    ) {
      handleSelect(initialConversationId)
    }
  }, [entries, initialConversationId, selectedId, handleSelect])

  useEffect(() => {
    setStoreSelectedId(selectedId)
    return () => setStoreSelectedId(null)
  }, [selectedId, setStoreSelectedId])

  useEffect(() => {
    if (!resizing) return
    const onMove = (e: globalThis.MouseEvent): void => {
      const diff = e.clientX - resizing.startX
      if (resizing.side === 'list') {
        setListWidth((_) => Math.min(Math.max(resizing.startWidth + diff, 220), 520))
      } else {
        setDetailsWidth((_) => Math.min(Math.max(resizing.startWidth - diff, 280), 640))
      }
    }
    const onUp = (): void => setResizing(null)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [resizing])

  useEffect(() => {
    localStorage.setItem('kc3-chat-list-width', String(listWidth))
  }, [listWidth])

  useEffect(() => {
    localStorage.setItem('kc3-chat-details-width', String(detailsWidth))
  }, [detailsWidth])

  const channelCount = (key: ChatChannel): number => {
    if (key === 'all') return counts.allChannels
    return counts[key]
  }

  const filterCount = (key: ChatStatus): number => {
    if (key === 'all') return counts.all
    return counts[key]
  }

  const isAssignedToMe = Boolean(
    selectedEntry && user?.agentId && selectedEntry.assignedAgentId === user.agentId,
  )
  const isAiAssigned = Boolean(selectedEntry?.aiAssigned)

  const teamOptions = useMemo(
    () => assignableTeams.map((t) => ({ value: t.id, label: t.name })),
    [assignableTeams],
  )
  const agentOptions = useMemo(
    () =>
      assignableAgents
        .filter((a) => !selectedTransferTeam || a.teamIds.includes(selectedTransferTeam))
        .map((a) => ({
          value: a.agentId,
          label: a.label,
          disabled: a.agentId === user?.agentId,
        })),
    [assignableAgents, selectedTransferTeam, user?.agentId],
  )

  const handleTransfer = useCallback(async () => {
    if (!selectedEntry || !selectedTransferAgent || isEmptyHtml(transferReason)) return
    const text = htmlToText(transferReason).trim() || undefined
    const res = await transfer({
      toAgentId: selectedTransferAgent,
      ...(text ? { reason: text } : {}),
    })
    if (res) {
      toast.success('Chat transferred')
      setTransferDrawerOpen(false)
      setSelectedTransferTeam('')
      setTransferReason('')
      setSelectedTransferAgent('')
      await refetchTransfers()
      await refetch()
    } else {
      toast.error('Failed to transfer chat')
    }
  }, [selectedEntry, selectedTransferAgent, transferReason, transfer, toast, refetch, refetchTransfers])

  const handleTransferToAi = useCallback(async () => {
    if (!selectedEntry) return
    setTransferringToAi(true)
    try {
      await api.post(`/agent/chats/${selectedEntry.id}/transfer-to-ai?channel=${encodeURIComponent(selectedEntry.channel)}`, {})
      toast.success('Chat transferred to AI Agent')
      setTransferDrawerOpen(false)
      setSelectedTransferTeam('')
      setTransferReason('')
      setSelectedTransferAgent('')
      await refetchTransfers()
      await refetch()
    } catch (err: unknown) {
      const error = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to transfer to AI'
      toast.error(error)
    } finally {
      setTransferringToAi(false)
    }
  }, [selectedEntry, toast, refetch, refetchTransfers])

  const thread = (() => {
    if (!selectedId || !selectedEntry) return null
    const aiAgentName = selectedEntry.aiAgentName ?? null
    switch (selectedEntry.channel) {
      case 'chat':
        return <LiveChatThread chatId={selectedId} onOpenDetails={() => setShowDetails(true)} isAssignedToMe={isAssignedToMe} isAiAssigned={isAiAssigned} aiAgentName={aiAgentName} onOpenTransfer={() => setTransferDrawerOpen(true)} onChanged={refetch} />
      case 'whatsapp':
        return <WhatsAppChatThread chatId={selectedId} onCall={(input) => void waCall.placeCall({ chatId: input.chatId, label: input.label })} callDisabled={waCall.isBusy || !waAccount?.connected} onChanged={refetch} isAssignedToMe={isAssignedToMe} isAiAssigned={isAiAssigned} aiAgentName={aiAgentName} onOpenTransfer={() => setTransferDrawerOpen(true)} onOpenDetails={() => setShowDetails(true)} />
      case 'facebook':
        return <FacebookChatThread chatId={selectedId} isAssignedToMe={isAssignedToMe} isAiAssigned={isAiAssigned} aiAgentName={aiAgentName} onOpenTransfer={() => setTransferDrawerOpen(true)} onOpenDetails={() => setShowDetails(true)} />
      case 'instagram':
        return <InstagramChatThread chatId={selectedId} isAssignedToMe={isAssignedToMe} isAiAssigned={isAiAssigned} aiAgentName={aiAgentName} onOpenTransfer={() => setTransferDrawerOpen(true)} onOpenDetails={() => setShowDetails(true)} />
      case 'telegram':
        return <TelegramChatThread chatId={selectedId} isAssignedToMe={isAssignedToMe} isAiAssigned={isAiAssigned} aiAgentName={aiAgentName} onOpenTransfer={() => setTransferDrawerOpen(true)} onOpenDetails={() => setShowDetails(true)} onChanged={refetch} />
      default:
        return null
    }
  })()

  const filterBar = (
    <div className="space-y-3">
      <div className="flex gap-1.5 overflow-x-auto whitespace-nowrap py-1 pb-1.5 scrollbar-thin">
        {CHANNELS.map((ch) => {
          const active = channel === ch.key
          const Icon = ch.icon
          return (
            <button
              key={ch.key}
              type="button"
              onClick={() => {
                const path = ch.key === 'chat' ? '/agent/live-chat' : `/agent/chats/${ch.key}`
                navigate(path)
              }}
              className={[
                'flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                active
                  ? `${ch.activeBg} text-white`
                  : `bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 ${ch.color}`,
              ].join(' ')}
            >
              <Icon className={active ? 'h-3.5 w-3.5 text-white' : `h-3.5 w-3.5 ${ch.color}`} />
              <span>{ch.label}</span>
              <span className={active ? 'text-white/80' : 'text-slate-400'}>({channelCount(ch.key)})</span>
            </button>
          )
        })}
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search conversations..."
          className="w-full rounded-lg bg-slate-100 py-1.5 pl-8 pr-8 text-xs text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:bg-white focus:ring-1 focus:ring-blue-500"
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          aria-label="Filter"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex items-center gap-3 border-b border-slate-100 pb-2">
        {FILTERS.map((f) => {
          const active = status === f.key
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatus(f.key)}
              className={[
                'relative pb-1 text-[11px] font-medium transition-colors',
                active ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700',
              ].join(' ')}
            >
              {f.label} ({filterCount(f.key)})
              {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-blue-600" />}
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className="flex h-full overflow-hidden">
      <ChatConversationList
        conversations={entries}
        selectedId={selectedId}
        loading={loading}
        onSelect={handleSelect}
        title="Chat"
        filterBar={filterBar}
        className={selectedId ? 'hidden md:flex' : 'flex'}
        style={{ width: listWidth }}
      />
      <div
        className="hidden w-1 shrink-0 cursor-col-resize touch-none select-none bg-slate-300 hover:bg-blue-300/50 active:bg-blue-400/50 md:block"
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setResizing({ side: 'list', startX: e.clientX, startWidth: listWidth }) }}
        role="separator"
        aria-label="Resize chat list"
      />

      {thread ? (
        <div className={`flex h-full flex-1 flex-col overflow-hidden ${selectedId ? 'flex' : 'hidden md:flex'}`}>
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="flex items-center gap-1.5 border-b border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 md:hidden"
          >
            <span>&larr; Back</span>
          </button>
          {thread}
        </div>
      ) : (
        <div className="hidden h-full flex-1 flex-col items-center justify-center bg-slate-50 text-slate-400 md:flex">
          <p className="text-sm">Select a chat to start.</p>
        </div>
      )}

      {selectedEntry && showDetails && (
        <>
          <div
            className="hidden w-1 shrink-0 cursor-col-resize touch-none select-none bg-slate-300 hover:bg-blue-300/50 active:bg-blue-400/50 xl:block"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setResizing({ side: 'details', startX: e.clientX, startWidth: detailsWidth }) }}
            role="separator"
            aria-label="Resize client details"
          />
          <ChatDetailsPanel
            entry={selectedEntry}
            onClose={() => setShowDetails(false)}
            transfers={transfers}
            onAssign={refetch}
            style={{ width: detailsWidth }}
          />
        </>
      )}

      <ActionDrawer
        open={transferDrawerOpen}
        onClose={() => {
          setTransferDrawerOpen(false)
          setSelectedTransferTeam('')
          setSelectedTransferAgent('')
          setTransferReason('')
        }}
        title="Transfer"
        subtitle="Reassign this chat"
        icon={<ArrowRightLeft className="h-4 w-4" />}
        info={{
          lead: 'Hands this chat to another team or agent, with a note explaining why.',
          points: [
            { term: 'Team', detail: 'Required — where the chat lands.' },
            { term: 'Agent', detail: 'Required. The current assignee is listed but disabled.' },
            { term: 'Transfer reason', detail: 'Required — the handoff note.' },
          ],
          caution: 'The chat leaves your queue as soon as you transfer it.',
        }}
        width={760}
        footer={
          <>
            <GhostButton onClick={() => { setTransferDrawerOpen(false); setSelectedTransferTeam(''); setSelectedTransferAgent(''); setTransferReason('') }}>
              Cancel
            </GhostButton>
            <PrimaryButton onClick={() => void handleTransfer()} disabled={!selectedTransferTeam || !selectedTransferAgent || isEmptyHtml(transferReason) || transferring}>
              {transferring ? 'Transferring…' : 'Submit'}
            </PrimaryButton>
          </>
        }
      >
        <div className="space-y-4">
          {/* Transfer to AI Agent — no form fields needed */}
          <div className="flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm font-semibold text-purple-900">Transfer to AI Agent</p>
                <p className="text-xs text-purple-600">Let the AI handle this conversation automatically</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void handleTransferToAi()}
              disabled={transferringToAi}
              className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
            >
              {transferringToAi ? 'Transferring…' : 'Transfer to AI'}
            </button>
          </div>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">or transfer to a human agent</span>
            </div>
          </div>

          <Field label="Team" required>
            <SelectField
              icon={<Users className="h-4 w-4" />}
              value={selectedTransferTeam}
              onChange={(v) => {
                setSelectedTransferTeam(v)
                setSelectedTransferAgent('')
              }}
              placeholder="Select Team"
              options={teamOptions}
            />
          </Field>

          <Field label="Agent" required>
            <SelectField
              value={selectedTransferAgent}
              onChange={setSelectedTransferAgent}
              placeholder={selectedTransferTeam ? 'Select Agent' : 'Select a team first'}
              options={agentOptions}
              disabled={!selectedTransferTeam}
            />
          </Field>

          <Field label="Transfer reason" required>
            <RichTextEditor
              value={transferReason}
              onChange={setTransferReason}
              height={160}
              readOnly={false}
              placeholder="Add a transfer reason…"
            />
          </Field>
        </div>
      </ActionDrawer>

      <WhatsAppCallOverlay
        state={waCall.state}
        call={waCall.activeCall}
        muted={waCall.muted}
        durationSeconds={waCall.durationSeconds}
        error={waCall.error}
        onAccept={() => waCall.activeCall && void waCall.acceptCall(waCall.activeCall)}
        onReject={() => waCall.activeCall && void waCall.rejectCall(waCall.activeCall.call_id)}
        onHangUp={() => void waCall.hangUp()}
        onToggleMute={waCall.toggleMute}
      />
    </div>
  )
}
